import { mockRequest } from './mock';
import type {
  KbArticle,
  KbCategory,
  KbTerm,
  KbTermInput,
  AiAnswer,
  AiConversation,
  AiHistory,
  AiTurn,
  Dashboard,
  DocumentPage,
  DocumentRow,
  Folder,
  FolderTree,
  LoginResponse,
  Upload,
  User,
  Well,
} from './types';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';
const TOKEN_KEY = 'wxp.token';

/**
 * The client answers its own requests from src/lib/mock.ts, so the UI runs with
 * nothing else started. Set VITE_MOCK_API=false once the Go backend is up.
 */
const USE_MOCK = (import.meta.env.VITE_MOCK_API ?? 'true') !== 'false';

/** Exposed so the sign-in page can say which accounts actually work. */
export const usingMockApi = USE_MOCK;

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — session-only auth */
  }
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');

  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  if (USE_MOCK) {
    const { status, body } = await mockRequest(path, init, token);
    if (status >= 400) {
      const err = body as { error?: string };
      throw new ApiError(err?.error ?? `Request failed (${status})`, status);
    }
    return body as T;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers });
  } catch {
    throw new ApiError('Cannot reach the WellXP server. Is the backend running?', 0);
  }

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => ({}));

  // A 401 is a session the server no longer knows — most often because the
  // API restarted and sessions live in its memory. Leaving the person on a
  // page where every action answers "forbidden" explains nothing; sending them
  // to sign in again does. The sign-in call itself is exempt, or a wrong
  // password would reload the page. Where they were goes along as ReturnUrl,
  // so signing back in lands on the same rail and folder, not on Overview.
  if (res.status === 401 && path !== '/auth/login') {
    setToken(null);
    if (window.location.pathname !== '/login') {
      const here = window.location.pathname + window.location.search;
      window.location.assign(`/login?ReturnUrl=${encodeURIComponent(here)}`);
    }
  }

  if (!res.ok) {
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return body as T;
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<User>('/auth/me'),

  /** Best-effort: the server forgets the session, the client forgets it either way. */
  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  /**
   * The API answers collections as {items, total} while the mock used to answer
   * a bare array. Unwrapping here rather than at every call site means one
   * shape reaches the components — and a mismatch cannot take the whole app
   * down, which is exactly what it did: Header called .find on an object.
   */
  wells: async () => {
    const res = await request<Well[] | { items: Well[] }>('/wells');
    return Array.isArray(res) ? res : (res?.items ?? []);
  },

  dashboard: (wellId: number) => request<Dashboard>(`/wells/${wellId}/dashboard`),

  /** The well's folder tree, already nested, with document counts attached. */
  folders: (wellId: number) => request<FolderTree>(`/wells/${wellId}/folders`),

  /**
   * One page of a folder's documents. For a smart folder the server runs its
   * rule instead of reading rows, which the client never has to know.
   */
  documents: (folderId: number, page: number, perPage: number) =>
    request<DocumentPage>(`/folders/${folderId}/documents?page=${page}&perPage=${perPage}`),

  /**
   * Step one of an upload: the API creates the row and signs a URL. It never
   * sees the file itself.
   */
  startUpload: (folderId: number, body: { name: string; contentType: string }) =>
    request<Upload>(`/folders/${folderId}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Step three: the bytes are in object storage, so the row becomes visible. */
  completeUpload: (documentId: number, sizeBytes: number) =>
    request<DocumentRow>(`/documents/${documentId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ sizeBytes }),
    }),

  /**
   * Arranging the tree — the superadmin's job. The server checks the role
   * again; hiding the buttons is a courtesy, not the guard.
   */
  createFolder: (wellId: number, body: { name: string; parentId?: number | null }) =>
    request<Folder>(`/wells/${wellId}/folders`, {
      method: 'POST',
      body: JSON.stringify({ name: body.name, parentId: body.parentId ?? null }),
    }),

  renameFolder: (folderId: number, name: string) =>
    request<Folder>(`/folders/${folderId}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  /**
   * What the folder is for, in the superadmin's words. Saving it puts the
   * folder's documents back in the reading queue on the server, because every
   * piece read from them carries this text.
   */
  describeFolder: (folderId: number, description: string) =>
    request<Folder>(`/folders/${folderId}`, {
      method: 'PATCH',
      body: JSON.stringify({ description }),
    }),

  deleteFolder: (folderId: number) =>
    request<void>(`/folders/${folderId}`, { method: 'DELETE' }),

  /**
   * Ask the assistant. The whole conversation goes up each time — the panel is
   * what holds it — and the model is reached from the server, never from here:
   * the API key belongs nowhere near a bundle the browser can read.
   */
  aiChat: (wellId: number, messages: AiTurn[], conversationId?: number) =>
    request<AiAnswer>(`/wells/${wellId}/ai/chat`, {
      method: 'POST',
      body: JSON.stringify({ messages, conversationId: conversationId ?? 0 }),
    }),

  /** Your own threads — or everyone's, when the server says you may. */
  aiHistory: (limit = 50) => request<AiHistory>(`/ai/conversations?limit=${limit}`),

  aiThread: (id: number) => request<AiConversation>(`/ai/conversations/${id}`),

  // ---- knowledge base: the superadmin's shelf for the assistant ----

  kbCategories: async () => (await request<{ items: KbCategory[] }>('/kb/categories')).items,

  kbTerms: async (q = '', category = '') => {
    const params = new URLSearchParams({ limit: '200' });
    if (q) params.set('q', q);
    if (category) params.set('category', category);
    return (await request<{ items: KbTerm[] }>(`/kb/terms?${params}`)).items;
  },

  kbCreateTerm: (body: KbTermInput) =>
    request<KbTerm>('/kb/terms', { method: 'POST', body: JSON.stringify(body) }),

  kbUpdateTerm: (slug: string, body: KbTermInput) =>
    request<KbTerm>(`/kb/terms/${encodeURIComponent(slug)}`, { method: 'PUT', body: JSON.stringify(body) }),

  kbDeleteTerm: (slug: string) =>
    request<void>(`/kb/terms/${encodeURIComponent(slug)}`, { method: 'DELETE' }),

  kbArticles: async () => (await request<{ items: KbArticle[] }>('/kb/articles')).items,

  kbCreateArticle: (body: { title: string; body: string }) =>
    request<KbArticle>('/kb/articles', { method: 'POST', body: JSON.stringify(body) }),

  kbUpdateArticle: (id: number, body: { title: string; body: string }) =>
    request<KbArticle>(`/kb/articles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  kbDeleteArticle: (id: number) => request<void>(`/kb/articles/${id}`, { method: 'DELETE' }),

  /** A short-lived signed URL. Requires object storage to be configured. */
  downloadUrl: (documentId: number) =>
    request<{ url: string; expiresAt: string }>(`/documents/${documentId}/download`),
};
